import { useTranslation } from "react-i18next"

import type { AlertSettingAccordion, AlertSettingType } from "types/alerts.types"

import {
  useDeviceAlertTypesQuery,
  usePowerlineAlertTypesQuery,
  useTowerAlertTypesQuery,
} from "features/api"

interface UseEntityAlertTypesReturn {
  alertTypes: AlertSettingType[] | undefined
  isLoading: boolean
  isError: boolean
}

// Each entity has its own alert types, and with them the params a setting carries. A
// device id narrows the list to the types that apply to that device.
const useEntityAlertTypes = (
  entity: AlertSettingAccordion["entity"],
  deviceId?: number,
): UseEntityAlertTypesReturn => {
  const { i18n } = useTranslation()
  const params = { lang: i18n.resolvedLanguage ?? "en" }

  const deviceQuery = useDeviceAlertTypesQuery(
    { lessId: deviceId ?? "-", params },
    { skip: entity !== "device" },
  )
  const powerlineQuery = usePowerlineAlertTypesQuery(
    { params },
    { skip: entity !== "powerline" },
  )
  const towerQuery = useTowerAlertTypesQuery({ params }, { skip: entity !== "tower" })

  switch (entity) {
    case "device":
      return {
        alertTypes: deviceQuery.data,
        isLoading: deviceQuery.isLoading,
        isError: deviceQuery.isError,
      }
    case "powerline":
      return {
        alertTypes: powerlineQuery.data,
        isLoading: powerlineQuery.isLoading,
        isError: powerlineQuery.isError,
      }
    case "tower":
      return {
        alertTypes: towerQuery.data,
        isLoading: towerQuery.isLoading,
        isError: towerQuery.isError,
      }
    default:
      return { alertTypes: undefined, isLoading: false, isError: false }
  }
}

export default useEntityAlertTypes
